import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { SurveyFormBuilder } from '../components/SurveyFormBuilder';
import { SurveyFieldGroupsManager } from '../components/SurveyFieldGroupsManager';
import { SurveyFormVersionViewDialog } from '../components/SurveyFormVersionViewDialog';
import { AssessmentChangeLog, FormStructure, SurveyForm } from '../features/surveys/types';
import { Icon } from '@iconify/react';
import {
  loadSurveyForms,
  addSurveyForm,
  clearForms,
  activateSurveyFormVersion,
} from '../features/surveys/surveyFormsSlice';
import { loadSurveyById, editSurvey, clearCurrentSurvey } from '../features/surveys/surveysSlice';
import { getAssessmentChangeLogs } from '../features/surveys/surveysApi';
import { loadFieldGroups } from '../features/surveys/surveyFieldGroupsSlice';
import { RootState } from '../store/store';
import { showToast } from '../components/ui/toast.utils';

const CHANGE_FIELD_LABELS: Record<string, string> = {
  title: 'Nombre',
  description: 'Descripción',
  category: 'Categoría',
  type: 'Tipo',
  periodType: 'Período',
  visibility: 'Visibilidad',
  active: 'Activo',
  status: 'Estado',
  visibleRoles: 'Roles con lectura',
  editableRoles: 'Roles con edición',
  visibleGroupIds: 'Grupos con lectura',
  editableGroupIds: 'Grupos con edición',
  createdById: 'Responsable',
};

function formatChangedFields(fields: string[]) {
  return fields.map((field) => CHANGE_FIELD_LABELS[field] ?? field).join(', ');
}

export function SurveyFormsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { items: forms, loading } = useSelector((state: RootState) => state.surveyForms);
  const { currentSurvey } = useSelector((state: RootState) => state.surveys);
  const { items: fieldGroups } = useSelector((state: RootState) => state.surveyFieldGroups);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showGroups, setShowGroups] = useState<string | null>(null);
  const [formError, setFormError] = useState<string>('');
  /** null = vista previa alineada con la revisión vigente */
  const [previewFormId, setPreviewFormId] = useState<string | null>(null);
  const [activatingFormId, setActivatingFormId] = useState<string | null>(null);
  const [viewForm, setViewForm] = useState<SurveyForm | null>(null);
  const [changeLogs, setChangeLogs] = useState<AssessmentChangeLog[]>([]);

  const loadChangeLogs = async (surveyId: string) => {
    try {
      setChangeLogs(await getAssessmentChangeLogs(surveyId));
    } catch {
      setChangeLogs([]);
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(loadSurveyById(id));
      dispatch(loadSurveyForms(id));
      dispatch(loadFieldGroups({ surveyId: id }));
      void loadChangeLogs(id);
    }

    return () => {
      dispatch(clearForms());
      dispatch(clearCurrentSurvey());
    };
  }, [dispatch, id]);

  const vigenteForm = useMemo(
    () => forms.find((f) => f.active) ?? forms[0] ?? null,
    [forms]
  );

  const displayForm = useMemo(() => {
    if (!previewFormId) return vigenteForm;
    return forms.find((f) => f.id === previewFormId) ?? vigenteForm;
  }, [forms, previewFormId, vigenteForm]);

  const groupNameById = useMemo(() => {
    const m = new Map<string, string>();
    fieldGroups.forEach((g) => m.set(g.id, g.name));
    return m;
  }, [fieldGroups]);

  useEffect(() => {
    if (previewFormId && !forms.some((f) => f.id === previewFormId)) {
      setPreviewFormId(null);
    }
  }, [forms, previewFormId]);

  const handleSetVigente = async (formId: string) => {
    if (!id || formId === vigenteForm?.id) return;
    try {
      setActivatingFormId(formId);
      await dispatch(activateSurveyFormVersion({ surveyId: id, formId })).unwrap();
      showToast('Revisión vigente actualizada. Las nuevas respuestas usarán esta plantilla.', 'success');
      setPreviewFormId(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'No se pudo cambiar la revisión vigente';
      showToast(msg, 'error');
    } finally {
      setActivatingFormId(null);
    }
  };

  const handleSaveForm = async (structure: FormStructure, isDraft: boolean = false) => {
    if (!id) return;

    try {
      setFormError('');
      
      // 1. Guardar la estructura del formulario (nueva revisión solo si cambia el diseño)
      const formResult = await dispatch(
        addSurveyForm({
          surveyId: id,
          payload: { structure },
        })
      );

      if (addSurveyForm.fulfilled.match(formResult)) {
        const surveyResult = await dispatch(
          editSurvey({
            id,
            payload: { status: isDraft ? 'draft' : 'published' },
          })
        );

        if (editSurvey.fulfilled.match(surveyResult)) {
          showToast(isDraft ? 'Borrador guardado correctamente' : 'Encuesta publicada correctamente', 'success');
          setShowBuilder(false);
          dispatch(loadSurveyForms(id));
          dispatch(loadSurveyById(id));
          void loadChangeLogs(id);
        } else {
          setFormError('Error al actualizar el estado de la encuesta');
        }
      } else {
        setFormError(formResult.error?.message || 'Error al guardar el formulario');
      }
    } catch (err: any) {
      setFormError(err.message || 'Ocurrió un error inesperado');
    }
  };

  if (!id) return <div>ID de encuesta no válido</div>;

  if (showBuilder) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Configurar Formulario</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Encuesta: {currentSurvey?.title}
            </p>
          </div>
          <button className="btn btn-secondary" onClick={() => setShowBuilder(false)}>
            ← Volver
          </button>
        </div>

        <SurveyFormBuilder
          initialStructure={vigenteForm?.structure}
          onSubmit={handleSaveForm}
          onCancel={() => setShowBuilder(false)}
          error={formError}
          isPublished={currentSurvey?.status === 'published'}
        />
      </div>
    );
  }

  if (showGroups) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Gestionar Agrupaciones</h1>
          <button className="btn btn-secondary" onClick={() => setShowGroups(null)}>
            ← Volver
          </button>
        </div>
        <SurveyFieldGroupsManager surveyId={id} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SurveyFormVersionViewDialog
        open={!!viewForm}
        onClose={() => setViewForm(null)}
        form={viewForm}
        structure={viewForm?.structure ?? null}
        groupNameById={groupNameById}
      />
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Gestión de Encuesta</h1>
          {currentSurvey && (
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-muted-foreground">{currentSurvey.title}</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                currentSurvey.status === 'published' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
              }`}>
                {currentSurvey.status === 'published' ? 'Publicada' : 'Borrador'}
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={() => navigate(window.location.pathname.startsWith('/evaluations') ? '/evaluations' : '/surveys')}>
            ← Volver
          </button>
          <button className="btn btn-outline" onClick={() => setShowGroups('survey')}>
            <Icon icon="mdi:layers-outline" className="w-5 h-5 mr-2" />
            Agrupaciones
          </button>
          <button className="btn btn-primary" onClick={() => setShowBuilder(true)}>
            {vigenteForm ? 'Editar Formulario' : 'Crear Formulario'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      )}
      {!loading && !vigenteForm && (
        <div className="border border-dashed border-border rounded-lg p-12 text-center bg-card">
          <Icon icon="mdi:form-select" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No hay un formulario configurado</h3>
          <p className="text-muted-foreground mb-6">
            Para que los usuarios puedan responder la encuesta, primero debes definir los campos.
          </p>
          <button className="btn btn-primary" onClick={() => setShowBuilder(true)}>
            Configurar Primer Formulario
          </button>
        </div>
      )}
      {!loading && vigenteForm && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Icon icon="mdi:history" className="w-6 h-6 text-primary shrink-0" />
              Historial de revisiones (F4)
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Todas las revisiones se conservan. La vigente es la que reciben las nuevas respuestas; al publicar un
              cambio estructural se crea una nueva revisión y pasa a ser vigente automáticamente.
            </p>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 border-b border-border text-left">
                    <th className="p-3 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <Icon icon="mdi:counter" className="w-4 h-4 text-muted-foreground" />
                        Revisión
                      </span>
                    </th>
                    <th className="p-3 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <Icon icon="mdi:calendar-clock" className="w-4 h-4 text-muted-foreground" />
                        Actualizado
                      </span>
                    </th>
                    <th className="p-3 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <Icon icon="mdi:comment-text-outline" className="w-4 h-4 text-muted-foreground" />
                        Respuestas
                      </span>
                    </th>
                    <th className="p-3 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <Icon icon="mdi:flag-outline" className="w-4 h-4 text-muted-foreground" />
                        Estado
                      </span>
                    </th>
                    <th className="p-3 font-medium w-[1%] whitespace-nowrap text-right">
                      <span className="inline-flex items-center gap-1.5 justify-end w-full">
                        <Icon icon="mdi:gesture-tap-button" className="w-4 h-4 text-muted-foreground" />
                        Acciones
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {forms.map((f) => {
                    const isRowPreview = displayForm?.id === f.id;
                    const isVigente = f.active;
                    return (
                      <tr
                        key={f.id}
                        className={`border-b border-border/80 last:border-0 cursor-pointer transition-colors ${
                          isRowPreview ? 'bg-primary/5' : 'hover:bg-muted/20'
                        }`}
                        onClick={() => setPreviewFormId((prev) => (prev === f.id ? null : f.id))}
                      >
                        <td className="p-3 font-medium">R{f.revision ?? f.version}</td>
                        <td className="p-3 text-muted-foreground">{new Date(f.updatedAt).toLocaleString()}</td>
                        <td className="p-3">{f._count?.responses ?? 0}</td>
                        <td className="p-3">
                          {isVigente ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <Icon icon="mdi:check-decagram" className="w-3.5 h-3.5" />
                              Vigente
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                              <Icon icon="mdi:archive-outline" className="w-3.5 h-3.5" />
                              Histórica
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <div
                            className="inline-flex items-center gap-1 justify-end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm px-2"
                              title="Ver formulario (solo lectura)"
                              aria-label="Ver formulario (solo lectura)"
                              onClick={() => setViewForm(f)}
                            >
                              <Icon icon="mdi:eye-outline" className="w-5 h-5" />
                            </button>
                            {!isVigente && (
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm px-2"
                                title="Usar como vigente"
                                aria-label="Usar como vigente"
                                disabled={!!activatingFormId}
                                onClick={() => void handleSetVigente(f.id)}
                              >
                                {activatingFormId === f.id ? (
                                  <span className="inline-block w-5 h-5 animate-pulse text-muted-foreground">…</span>
                                ) : (
                                  <Icon icon="mdi:bookmark-check-outline" className="w-5 h-5 text-primary" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Clic en una fila para previsualizar su estructura. Las revisiones históricas no se eliminan.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Icon icon="mdi:eye-outline" className="text-primary" />
              Vista previa de estructura
              {displayForm && (
                <span className="text-sm font-normal text-muted-foreground">(R{displayForm.revision ?? displayForm.version})</span>
              )}
            </h3>
            {displayForm && previewFormId && displayForm.id !== vigenteForm?.id && (
              <p className="text-xs text-amber-700 dark:text-amber-300 mb-3 rounded-md bg-amber-50 dark:bg-amber-950/40 px-3 py-2 border border-amber-200/60 dark:border-amber-800/50">
                Estás viendo una revisión histórica. Las nuevas respuestas siguen usando la revisión vigente (R
                {vigenteForm?.revision ?? vigenteForm?.version}).
              </p>
            )}
            <div className="space-y-3">
              {displayForm?.structure.fields.map((field: any, idx: number) => (
                <div key={field.id} className="flex items-start gap-3 p-3 rounded-md bg-muted/30 border border-border/50">
                  <span className="text-xs font-bold text-muted-foreground mt-1">{idx + 1}</span>
                  <div>
                    <p className="text-sm font-medium">{field.label}</p>
                    <p className="text-xs text-muted-foreground uppercase">{field.type}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Icon icon="mdi:information-outline" className="text-primary" />
                Resumen
              </h3>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Revisión en vista:</span>
                  <span className="font-medium">R{displayForm?.revision ?? displayForm?.version}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Revisión vigente:</span>
                  <span className="font-medium">R{vigenteForm.revision ?? vigenteForm.version}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total de campos:</span>
                  <span className="font-medium">{displayForm?.structure.fields.length ?? 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Respuestas (esta revisión):</span>
                  <span className="font-medium">{displayForm?._count?.responses || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Última actualización:</span>
                  <span className="font-medium">
                    {displayForm ? new Date(displayForm.updatedAt).toLocaleDateString() : '—'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Icon icon="mdi:clipboard-text-clock-outline" className="text-primary" />
                Historial de configuración
              </h3>
              {changeLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Sin cambios de configuración registrados todavía.
                </p>
              ) : (
                <ol className="space-y-3">
                  {changeLogs.slice(0, 5).map((log) => (
                    <li key={log.id} className="rounded-md border border-border/60 bg-muted/20 p-3">
                      <p className="text-xs text-muted-foreground">
                        {new Date(log.createdAt).toLocaleString()}
                      </p>
                      <p className="text-sm font-medium mt-1">{formatChangedFields(log.changedFields)}</p>
                    </li>
                  ))}
                </ol>
              )}
              {changeLogs.length > 5 && (
                <p className="text-xs text-muted-foreground mt-3">
                  Se muestran los últimos 5 cambios de configuración.
                </p>
              )}
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-2 text-primary">Sincronización con Teable</h3>
              <p className="text-sm text-muted-foreground mb-4">
                La integración con Teable está activa. Cualquier cambio en los campos se reflejará en la hoja de cálculo sin perder los datos existentes.
              </p>
              <div className="flex items-center gap-2 text-sm text-primary font-medium">
                <Icon icon="mdi:check-circle" />
                Conectado correctamente
              </div>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
